/* @flow */

import shaka from 'shaka-player';

import type { BasicFunction } from '../../../helpers/jsHelpers/types';

import { logWarning } from '../../../helpers/debug/debug';

// Time between two buffering check (in ms)
const BUFFERING_DETECTOR_INTERVAL = 250;

const RECENT_PROGRESS_DELAY = 1_000;

type BufferingEventHandler = (event: { buffering: boolean }) => void;

type BufferingEmitter = (buffering: boolean) => void;

export type UnifiedBufferingObserver = {
  destroy: BasicFunction,
  initialize: BasicFunction,
  isBuffering: () => boolean,
  onChange: (BufferingEmitter) => void,
};

type NativeBufferingDetector = {
  destroy: BasicFunction,
  isBuffering: () => boolean | null,
};

const createNativeBufferingDetector = (video: HTMLVideoElement, options: { onChange: (boolean) => void }): NativeBufferingDetector => {
  const { onChange } = options;

  let buffering: boolean | null = null;
  let intervalId: IntervalID | null = null;
  let lastProgressAt = performance.now();
  let rafId: number | null = null;

  const tick = () => {
    const now = performance.now();

    if (!video.paused && video.currentTime > 0) {
      lastProgressAt = now;
      set(false);
    }

    rafId = requestAnimationFrame(tick);
  };

  const set = (next: boolean) => {
    if (buffering === null || next !== buffering) {
      buffering = next;
      onChange(next);
    }
  };

  const onTimeUpdate = () => {
    lastProgressAt = performance.now();

    if (!video.paused) {
      set(false);
    }
  };

  const check = () => {
    const now = performance.now();
    const isPlaying = !video.paused;
    const noProgress = now - lastProgressAt > RECENT_PROGRESS_DELAY;

    if (isPlaying && noProgress) {
      set(true);
    }
  };

  video.addEventListener('timeupdate', onTimeUpdate);

  rafId = requestAnimationFrame(tick);
  intervalId = setInterval(check, BUFFERING_DETECTOR_INTERVAL);

  const destroy = () => {
    video.removeEventListener('timeupdate', onTimeUpdate);
    cancelAnimationFrame(rafId);
    clearInterval(intervalId);
  };

  return {
    destroy,
    isBuffering: () => buffering,
  };
};

const createShakaBufferingSource = (player: typeof shaka.Player, onChange: BufferingEmitter) => {
  let buffering: boolean | null = null;

  const handler: BufferingEventHandler = (event) => {
    if (buffering === null || event.buffering !== buffering) {
      ({ buffering } = event);
      onChange(buffering);
    }
  };

  player.addEventListener('buffering', handler);

  const destroy = () => {
    player.removeEventListener('buffering', handler);
  };

  return {
    destroy,
    isBuffering: () => buffering,
  };
};

const createUnifiedBufferingObserver = (player: typeof shaka.Player, video: HTMLVideoElement): UnifiedBufferingObserver => {
  let buffering: boolean | null = null;
  let listeners: Array<BufferingEmitter> = [];

  const emit = (value: boolean) => {
    if (buffering === null || value !== buffering) {
      buffering = value;
      listeners.forEach((cb) => cb(value));
    }
  };

  const onChange = (cb: BufferingEmitter) => {
    listeners.push(cb);
  };

  let source: NativeBufferingDetector | null = null;

  const initialize = () => {
    const mode = player.getLoadMode();

    switch (mode) {
      case shaka.Player.LoadMode.MEDIA_SOURCE:
        // MSE path (Chrome, DASH, etc.)
        source = createShakaBufferingSource(player, emit);
        break;
      case shaka.Player.LoadMode.SRC_EQUALS:
        // Safari native HLS / FairPlay path
        source = createNativeBufferingDetector(video, { onChange: emit });

        if (!video.paused && video.currentTime > 0) {
          // Playback already started
          emit(false);
        }

        break;
      case shaka.Player.LoadMode.DESTROYED:
      case shaka.Player.LoadMode.NOT_LOADED:
      default:
        logWarning(`[buffering] unexpected load mode: ${mode}`);
    }
  };

  const destroy = () => {
    source?.destroy?.();
    listeners = [];
  };

  return {
    destroy,
    initialize,
    isBuffering: () => buffering ?? false,
    onChange,
  };
};

export { createUnifiedBufferingObserver };
